import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../src/Components/Layout/ReusableTable";
import { FaSave } from "react-icons/fa";
import { AuthContext } from "./Components/AuthContext/Authcontext";
import baseURL from "./Components/Apiurs/Apiurls";

const LeadsTable = () => {
  const { userId } = useContext(AuthContext);
  console.log("User ID from context:", userId);
  const [leads, setLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState({}); // Store all employees for lookup
  const [selectedManager, setSelectedManager] = useState({});
  const [selectedAssociate, setSelectedAssociate] = useState({});
  const [managerChanged, setManagerChanged] = useState({});
  const [associateChanged, setAssociateChanged] = useState({});
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
    fetchManagers();
  }, []);


  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/addleads`);
      setLeads(response.data);
      
      // Initialize selectedManager and selectedAssociate with stored values
      const managerSelections = {};
      const associateSelections = {};
      
      response.data.forEach(lead => {
        if (lead.assign_to_manager) {
          managerSelections[lead.id] = lead.assign_to_manager.toString();
        }
        if (lead.assign_to_associate) {
          associateSelections[lead.id] = lead.assign_to_associate.toString();
        }
      });
      
      setSelectedManager(managerSelections);
      setSelectedAssociate(associateSelections);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/employees/managers`);
      setManagers(response.data.data);
      
      // Create an employees lookup object
      const employeesLookup = {};
      
      // Add managers to lookup
      response.data.data.forEach(manager => {
        employeesLookup[manager.id] = manager.name;
        
        // Add team members to lookup
        if (manager.teamMembers) {
          manager.teamMembers.forEach(member => {
            employeesLookup[member.id] = member.name;
          });
        }
      });
      
      setEmployees(employeesLookup);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  const handleManagerAssignment = async (leadId) => {
    try {
      const selectedId = selectedManager[leadId];
      if (!selectedId) {
        setMessage("Please select a manager");
        return;
      }

      await axios.put(
        `${baseURL}/api/addleads/${leadId}/assign-manager`,
        {
          assigned_from: userId,
          assigned_to: selectedId,
        }
      );

      setMessage("Manager assignment saved successfully");
      setManagerChanged(prev => ({ ...prev, [leadId]: false }));
      fetchLeads(); // Refresh the data
    } catch (error) {
      console.error("Error assigning manager:", error);
      setMessage(error.response?.data?.message || "Failed to save manager assignment");
    }
  };

  const handleAssociateAssignment = async (leadId) => {
    try {
      const assignedTo = selectedAssociate[leadId];
      if (!assignedTo) {
        setMessage("Please select an associate");
        return;
      }

      await axios.put(
        `${baseURL}/api/addleads/${leadId}/assign-associate`,
        {
          assigned_from: userId,
          assign_to: assignedTo,
        }
      );

      setMessage("Associate assignment saved successfully");
      setAssociateChanged(prev => ({ ...prev, [leadId]: false }));
      fetchLeads();
    } catch (error) {
      console.error("Error assigning associate:", error);
      setMessage(error.response?.data?.message || "Failed to save associate assignment");
    }
  };

  // Define columns for the table
  const columns = [
    {
      key: "id",
      title: "ID"
    },
    {
      key: "lead_name",
      title: "Name"
    },
    {
      key: "email",
      title: "Email"
    },
    {
      key: "contact_number",
      title: "Phone"
    },
    {
      key: "lead_source",
      title: "Source"
    },
    {
      key: "terms_conditions",
      title: "Terms and Conditions"
    },
    {
      key: "manager",
      title: "Assign Manager",
      render: (item) => {
        const leadId = item.id;
        const storedManagerId = item.assign_to_manager;
        
        return (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
            <select
              className="form-select form-select-sm"
              value={selectedManager[leadId] || storedManagerId || ""}
              onChange={(e) => {
                const newManagerId = e.target.value;
                setSelectedManager(prev => ({ ...prev, [leadId]: newManagerId }));
                setManagerChanged(prev => ({ ...prev, [leadId]: true }));
                
                // Reset associate selection when manager changes
                setSelectedAssociate(prev => {
                  const updated = { ...prev };
                  delete updated[leadId];
                  return updated;
                });
                setAssociateChanged(prev => ({ ...prev, [leadId]: false }));
              }}
            >
              <option value="">Select Manager</option>
              {managers.map(manager => {
                const isSelected = selectedManager[leadId] === manager.id.toString() || 
                                 storedManagerId === manager.id;
                return (
                  <option
                    key={manager.id}
                    value={manager.id}
                    style={{
                      fontWeight: isSelected ? 'bold' : 'normal',
                      backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                    }}
                  >
                    {manager.name}{isSelected ? ' (Assigned)' : ''}
                  </option>
                );
              })}
            </select>

            <button
              className="btn btn-sm btn-success"
              onClick={() => handleManagerAssignment(leadId)}
              disabled={!managerChanged[leadId]}
              title="Save Manager"
            >
              <FaSave />
            </button>
          </div>
        );
      }
    },
    {
      key: "associate",
      title: "Assign Associate",
      render: (item) => {
        const leadId = item.id;
        const storedAssociateId = item.assign_to_associate;
        const managerId = selectedManager[leadId] || item.assign_to_manager;
        const manager = managers.find(m => m.id.toString() === managerId?.toString());
        const teamMembers = manager?.teamMembers || [];
        
        return (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
            <select
              className="form-select form-select-sm"
              value={selectedAssociate[leadId] || storedAssociateId || ""}
              onChange={(e) => {
                const newAssociateId = e.target.value;
                setSelectedAssociate(prev => ({ ...prev, [leadId]: newAssociateId }));
                setAssociateChanged(prev => ({ ...prev, [leadId]: true }));
              }}
              disabled={!managerId}
            >
              <option value="">Select Associate</option>
              {teamMembers.map(member => {
                const isSelected = selectedAssociate[leadId] === member.id.toString() || 
                                 storedAssociateId === member.id;
                return (
                  <option
                    key={member.id}
                    value={member.id}
                    style={{
                      fontWeight: isSelected ? 'bold' : 'normal',
                      backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                    }}
                  >
                    {member.name}{isSelected ? ' (Assigned)' : ''}
                  </option>
                );
              })}
            </select>

            <button
              className="btn btn-sm btn-success"
              onClick={() => handleAssociateAssignment(leadId)}
              disabled={!associateChanged[leadId]}
              title="Save Associate"
            >
              <FaSave />
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2>Leads Table</h2>
        <button
          style={{ 
            marginBottom: "10px", 
            padding: "10px 15px",
            backgroundColor: "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer"
          }}
          onClick={() => navigate("/leadform")}
        >
          ➕ Add Lead
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes("successfully") ? "alert-success" : "alert-danger"}`} 
             style={{ marginBottom: "20px", padding: "10px" }}>
          {message}
        </div>
      )}

      <ReusableTable
        title="Leads"
        data={leads}
        columns={columns}
        initialEntriesPerPage={10}
        searchPlaceholder="Search leads..."
        showSearch={true}
        showEntriesSelector={true}
        showPagination={true}
      />
    </div>
  );
};

export default LeadsTable;